/**
 * Flattens a BlockNote document into plain text — ROADMAP 9.5.
 *
 * The journal stores the editor's own block array, because a note is a
 * DOCUMENT and flattening it on the way in would lose the formatting and
 * guess it on the way out. This exists for the two places that need the words
 * without the structure: the collapsed row's one-line preview, and the word
 * count under the writer.
 *
 * Inline runs concatenate with no separator — their spacing is already inside
 * the runs — while child blocks always become their own line, or words fuse
 * across a block boundary.
 *
 * Carried over from `trading-journal`, where the same helper fronts the same
 * editor. Depth-bounded because the input is a nested document and a cycle in
 * it must cost a truncated string rather than the tab.
 */

const MAX_DEPTH = 24

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function inlineText(content: unknown, depth = 0): string {
  if (depth > MAX_DEPTH) return ''

  if (Array.isArray(content)) {
    return content.map((node) => inlineText(node, depth + 1)).join('')
  }

  if (!isRecord(content)) return ''

  if (typeof content.text === 'string') return content.text

  if (content.content !== undefined) {
    return inlineText(content.content, depth + 1)
  }

  return ''
}

/** Captions and names are authored text; URLs are not, and indexing them makes
 *  a search for "png" match unrelated notes. */
function propsText(props: unknown): string {
  if (!isRecord(props)) return ''
  const parts: string[] = []
  for (const key of ['caption', 'name'] as const) {
    const value = props[key]
    if (typeof value === 'string' && value.trim()) parts.push(value.trim())
  }
  return parts.join(' ')
}

function collectLines(blocks: unknown, out: string[], depth: number): void {
  if (depth > MAX_DEPTH || !Array.isArray(blocks)) return

  for (const block of blocks) {
    if (!isRecord(block)) continue

    const parts = [inlineText(block.content), propsText(block.props)]
      .map((part) => part.replace(/\s+/g, ' ').trim())
      .filter(Boolean)

    if (parts.length > 0) out.push(parts.join(' '))

    if (block.children !== undefined) {
      collectLines(block.children, out, depth + 1)
    }
  }
}

export function blocksToPlainText(contentJson: unknown): string {
  if (!Array.isArray(contentJson)) return ''

  const lines: string[] = []
  collectLines(contentJson, lines, 0)

  return lines.join('\n').trim()
}

export function countWords(plainText: string): number {
  const trimmed = plainText.trim()
  if (!trimmed) return 0
  return trimmed.split(/\s+/).length
}

/**
 * Rebuilds a document from a plain-text field so the rich editor can open
 * something that was written before it existed (a `reviewNote` typed into the
 * old textarea, an imported trade note). Without this, opening the editor on
 * legacy content would show a blank page and the first save would erase it.
 */
export function plainTextToBlocks(text: string | null | undefined) {
  const trimmed = (text ?? '').trim()
  if (!trimmed) return undefined

  return trimmed
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => ({
      type: 'paragraph' as const,
      props: {},
      content: [{ type: 'text' as const, text: line, styles: {} }],
      children: [],
    }))
}
